import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import { AuthProvider } from '@/contexts/AuthContext';
import Logo from '@/components/redesign/Logo';
import Icon from '@/components/redesign/icons/Icons';
import AuthTrustPanel from '@/components/redesign/AuthTrustPanel';

const Alert = dynamic(() => import('@/components/utils/Alert'));

export default function ForgetPassword() {
  const title = 'Forget Password';
  const description =
    'Forgot your Medical Chat password? No problem. Enter your email address to receive a password reset link and instructions for resetting your password.';
  const canonical = 'https://medical.chat-data.com/forgot-password';
  const emailRef = useRef();
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setMessage('');
      setError('');
      if (emailRef.current.value === '') {
        return;
      }
      setLoading(true);
      const { sendResetPasswordEmail } = await import('@/libs/request');
      sendResetPasswordEmail({
        email: emailRef.current.value,
        lang: t('lang'),
      })
        .then(() => {
          setMessage(t('reset_password_sent'));
        })
        .catch((err) => {
          setError(t(err.message, err.param));
        })
        .finally(() => {
          emailRef.current.value = '';
        });
    } catch {
      setError(t('reset_password_failure'));
    }
    setLoading(false);
  };
  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content={title} />
        <meta property="og:site_name" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://medical.chat-data.com/demos/chatbot_en.png"
        />
        <meta property="og:image:width" content="1842" />
        <meta property="og:image:height" content="1204" />
        <meta property="og:image:alt" content={title} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@truechatdata" />
        <meta name="twitter:creator" content="@truechatdata" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta
          name="twitter:image"
          content="https://medical.chat-data.com/demos/chatbot_en.png"
        />
        <meta name="twitter:image:width" content="1842" />
        <meta property="twitter:image:height" content="1204" />
        <meta name="twitter:image:alt" content={title} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'BreadcrumbList',
                  '@id': `${canonical}/#breadcrumblist`,
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      '@id': 'https://medical.chat-data.com/#listItem',
                      position: 1,
                      name: 'Medical Chat | Medical AI Assistant',
                      item: 'https://medical.chat-data.com/',
                      nextItem: {
                        '@id': `${canonical}/#listItem`,
                      },
                    },
                    {
                      '@type': 'ListItem',
                      '@id': `${canonical}/#listItem`,
                      position: 2,
                      name: title,
                      item: `${canonical}/`,
                      previousItem: {
                        '@id': 'https://medical.chat-data.com/#listItem',
                      },
                    },
                  ],
                },
                {
                  '@type': 'WebPage',
                  '@id': `${canonical}/#webpage`,
                  url: canonical,
                  name: title,
                  description,
                  inLanguage: 'en-US',
                  isPartOf: {
                    '@id': 'https://medical.chat-data.com/#website',
                  },
                  breadcrumb: {
                    '@id': `${canonical}/#breadcrumblist`,
                  },
                },
              ],
            }),
          }}
        ></script>
      </Head>
      <AuthProvider user={null}>
        <div className="grid min-h-screen bg-surface-bg lg:grid-cols-2">
          {/* Left — form panel */}
          <div className="flex flex-col bg-white">
            <div className="border-b border-border px-6 py-[22px] sm:px-10">
              <Link href="/">
                <Logo />
              </Link>
            </div>

            <div className="flex flex-1 items-center justify-center p-6 sm:p-10">
              <div className="w-full max-w-[400px]">
                <h1 className="t-display mb-2 text-[32px]">
                  {t('reset_password_title')}
                </h1>
                <p className="mb-7 text-sm text-ink-600">
                  Enter your email and we&apos;ll send you a secure link to
                  reset your password.
                </p>

                <form className="grid gap-3" onSubmit={handleSubmit}>
                  <label className="block">
                    <div className="mb-[5px] text-xs font-semibold text-ink-700">
                      {t('email')}
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="off"
                      ref={emailRef}
                      required
                      placeholder="you@hospital.org"
                      className="w-full rounded-sm border border-border-strong bg-white px-3 py-2.5 font-sans text-[13.5px] text-ink-900 placeholder:text-ink-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </label>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-redesign btn-redesign-primary btn-redesign-lg mt-2 w-full justify-center"
                  >
                    {t('submit')}
                    <Icon.ArrowR width={14} height={14} />
                  </button>
                </form>

                {error && (
                  <div className="mt-3">
                    <Alert variant="danger">{error}</Alert>
                  </div>
                )}
                {message && (
                  <div className="mt-3">
                    <Alert variant="success">{message}</Alert>
                  </div>
                )}

                <div className="mt-6 text-center text-[13px] text-ink-600">
                  <Link
                    href="/signin"
                    className="font-semibold text-brand-800 hover:underline"
                  >
                    {t('sign_in')}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Right — trust panel */}
          <div className="hidden lg:block">
            <AuthTrustPanel />
          </div>
        </div>
      </AuthProvider>
    </>
  );
}

export async function getServerSideProps(context) {
  const { req, res } = context;
  if (req.isAuthenticated()) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    };
  }
  return {
    props: {},
  };
}
